import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/common/Seo";
import ManhuaCard from "@/components/feature/ManhuaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { manhuaList, categories } from "@/mocks/manhua";

type SortKey = "hot" | "rating" | "latest";

export default function ListPage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "全部";
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<SortKey>("hot");

  const filtered = useMemo(() => {
    let list = [...manhuaList];
    if (activeCat !== "全部") list = list.filter((m) => m.category === activeCat);
    if (keyword.trim()) list = list.filter((m) => (m.title + m.tags.join("") + m.intro).includes(keyword.trim()));
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "latest") list.sort((a, b) => a.updateTime.localeCompare(b.updateTime));
    return list;
  }, [activeCat, keyword, sort]);

  const setCat = (c: string) => {
    if (c === "全部") params.delete("cat");
    else params.set("cat", c);
    setParams(params, { replace: true });
  };

  return (
    <>
      <Seo
        title={activeCat === "全部" ? "全部好看韩漫 - 韩漫大全分类榜" : `${activeCat}韩漫推荐 - 韩漫大全`}
        description={`${activeCat}分类下的人气韩漫汇总，韩漫大全在线阅读站按题材、人气与评分排序，帮你找到好看又对口的韩漫作品。`}
        keywords="韩漫大全,韩漫分类,韩漫排行榜,好看韩漫"
        canonical="https://hmmread.github.io/list"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "韩漫大全分类榜",
          description: "按题材与人气排序的好看韩漫汇总。",
        }}
      />

      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全部韩漫" }]} />

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="heading-font text-2xl font-bold text-foreground-950 md:text-3xl">
              {activeCat === "全部" ? "全部好看韩漫" : `${activeCat}韩漫推荐`}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">按题材筛选，或搜索标题、标签，快速定位合口味的好看韩漫。</p>
          </div>
          <label className="flex items-center gap-2 rounded-full border border-background-300/60 bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索韩漫标题或标签"
              className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:w-52"
            />
          </label>
        </div>

        {/* 分类切换 */}
        <div className="mt-6 flex flex-wrap gap-2">
          {["全部", ...categories].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeCat === c ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* 排序 */}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-foreground-600">排序：</span>
          {(
            [
              { key: "hot", label: "人气" },
              { key: "rating", label: "评分" },
              { key: "latest", label: "最新" },
            ] as { key: SortKey; label: string }[]
          ).map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                sort === s.key ? "bg-secondary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-12 flex flex-col items-center gap-3 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300" />
            <p className="text-sm text-foreground-600">没有找到符合条件的好看韩漫，换个题材或关键词试试吧</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {filtered.map((m) => (
              <ManhuaCard key={m.id} item={m} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}